import { PopoverClose } from "./PopoverClose";
import { PopoverButton } from "./PopoverButton";
import { PopoverContent } from "./PopoverContent";
import { PopoverTrigger } from "./PopoverTrigger";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PopoverContext, PopoverContextProps } from "./internal/PopoverContext";
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  limitShift,
  offset,
  Placement,
  shift,
  size,
} from "@floating-ui/react";
import { safeHidePopover, safeShowPopover } from "../../internal/functions";
import { DisableCloseOnEscapeOrClickProps } from "../../types";
import {
  OpenCloseTransitionStateEffects,
  useOnClickOutside,
  useOptionallyControlledState,
  usePopoverTransitionStates,
} from "../../internal/hooks";
import { DialogContext } from "../Dialog/internal/DialogContext";

/**
 * Props for the Popover component
 * @property {ReactNode} [children] - The content to be rendered inside the popover
 * @property {string} [id] - Unique identifier for the popover
 * @property {boolean} [modal] - Whether the popover should be modal (traps focus)
 * @property {boolean} [openOnHover] - Changes interaction to use hover instead of click
 * @property {boolean} [fillAvailableHeight] - Changes height and positioning for select/combobox
 * @property {number | string} [maxHeight] - Maximum height of the popover content
 * @property {boolean} [fitScreen] - Whether the popover should fit within the screen bounds
 * @property {number | string} [minHeight] - Minimum height of the popover content
 * @property {boolean} [matchReferenceWidth] - Whether the popover width should match the trigger element
 * @property {boolean} [fixedWidth] - Whether the popover should have a fixed width
 * @property {string} [placement] - Placement of the popover relative to the trigger
 * @property {boolean} [open] - Controlled open state
 * @property {boolean} [defaultOpen] - Default open state for uncontrolled mode
 * @property {Function} [onOpenChange] - Callback when open state changes
 * @property {Function} [onClickOutside] - Callback when clicking outside the popover
 * @property {Function} [onClose] - Fires when the floating element is requested to be closed
 * @property {boolean} [disableCaret] - Whether to disable the caret/arrow indicator
 * @property {boolean} [noPadding] - Whether to remove default padding from content
 * @property {boolean} [disableAutoUpdate] - Whether to disable automatic position updates
 * @property {boolean} [disableTriggerFocus] - Whether to disable focus on the trigger element
 * @property {number} [delay] - Delay before opening the popover (for hover mode)
 * @property {boolean} [disableCloseOnClickOutside] - Whether to disable closing on outside click
 * @property {boolean} [disableCloseOnEscape] - Whether to disable closing on escape key
 */

/**
 * Props for the Popover component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type PopoverProps = {
  /**
   * The content to be rendered inside the popover
   */
  children?: ComponentPropsWithoutRef<"div">["children"];
  /**
   * Unique identifier for the popover
   */
  id?: ComponentPropsWithoutRef<"div">["id"];
  /**
   * Whether the popover should be modal (traps focus)
   * @default false
   */
  modal?: boolean;
  /**
   * Disables the shift of floating element
   * @internal
   */
  disableShift?: boolean;
  /**
   * Disables the flip of floating element
   * @internal
   */
  disableFlip?: boolean;
  /**
   * Disables the flip cross-axis fallback of floating element
   * @internal
   */
  disableFlipFallback?: boolean;
  /**
   * Changes interaction to use hover instead of click
   * @default false
   */
  openOnHover?: boolean;
  /**
   * Changes height and positioning for select/combobox
   * @default false
   */
  fillAvailableHeight?: boolean;
  /**
   * Maximum height of the popover content
   */
  maxHeight?: number | string;
  /**
   * Whether the popover should fit within the screen bounds
   * @default false
   */
  fitScreen?: boolean;
  /**
   * Minimum height of the popover content
   * @default "2.875rem"
   */
  minHeight?: number | string;
  /**
   * Whether the popover width should match the trigger element
   * @default false
   */
  matchReferenceWidth?: boolean;
  /**
   * Whether the popover should have a fixed width
   * @default false
   */
  fixedWidth?: boolean;
  /**
   * Placement of the popover relative to the trigger
   * @default "bottom"
   */
  placement?: Placement;
  /**
   * Controlled open state
   */
  open?: boolean;
  /**
   * Default open state for uncontrolled mode
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Callback when clicking outside the popover
   */
  onClickOutside?: (e: globalThis.MouseEvent) => void;
  /**
   * Fires when the floating element is requested to be closed
   * Note: this does not fire when openOnHover is true and mouse leaves to close
   * eg. on click outside, [esc] to close, or by <Popover.Close>
   */
  onClose?: () => void;
  /**
   * Whether to disable the caret/arrow indicator
   * @default false
   */
  disableCaret?: boolean;
  /**
   * Whether to remove default padding from content
   * @default false
   */
  noPadding?: boolean;
  /**
   * Whether to disable automatic position updates
   * @default false
   */
  disableAutoUpdate?: boolean;
  /**
   * Whether to disable focus on the trigger element
   * @default false
   */
  disableTriggerFocus?: boolean;
  /**
   * Delay before opening the popover (for hover mode)
   * @default 100
   */
  delay?: number;
} & DisableCloseOnEscapeOrClickProps &
  OpenCloseTransitionStateEffects;

/**
 * Popover component for displaying floating content relative to a trigger element.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Automatic positioning with fallback strategies
 * - Hover and click interaction modes
 * - Modal and non-modal variants
 * - Custom trigger elements
 * - Automatic height and width management
 * - Screen boundary detection and fitting
 * - Keyboard navigation and accessibility
 * - Outside click detection and handling
 * - Focus management and trapping
 * - Arrow/caret indicators
 * - Delay support for hover interactions
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Popover>
 *   <Popover.Button>Click to open</Popover.Button>
 *   <Popover.Content>
 *     <p>This is the popover content</p>
 *     <Popover.Close>Close</Popover.Close>
 *   </Popover.Content>
 * </Popover>
 *
 * @example
 * <Popover openOnHover delay={1000}>
 *   <Popover.Button>Hover to open</Popover.Button>
 *   <Popover.Content>Tooltip content</Popover.Content>
 * </Popover>
 *
 * @example
 * <Popover modal>
 *   <Popover.Trigger>
 *     {(props) => <button {...props}>Custom trigger</button>}
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <TextField label="Form field" />
 *     <Popover.Close>Done</Popover.Close>
 *   </Popover.Content>
 * </Popover>
 */
export const Popover = Object.assign(
  forwardRef(function PopoverInner(props: PopoverProps, ref) {
    const {
      children,
      openOnHover = false,
      delay = 100,
      placement = "bottom",
      minHeight = "2.875rem",
      open,
      defaultOpen = false,
      disableShift,
      disableFlip,
      disableFlipFallback,
      matchReferenceWidth,
      id,
      disableCaret,
      fixedWidth,
      fillAvailableHeight,
      maxHeight,
      disableCloseOnClickOutside,
      disableCloseOnEscape,
      fitScreen,
      disableTriggerFocus,
      modal,
      noPadding,
      onClickOutside,
      onClose,
      onOpenAnimationStart,
      onOpenAnimationComplete,
      onCloseAnimationStart,
      onCloseAnimationComplete,
    } = props;
    const uid = useId();
    const [invoker, setInvoker] =
      useState<PopoverContextProps["invoker"]>(undefined);
    const [popover, setPopover] =
      useState<PopoverContextProps["popover"]>(undefined);
    const [rootNode, setRootNode] = useState<PopoverContextProps["rootNode"]>();
    const [arrowElement, setArrowElement] =
      useState<PopoverContextProps["arrowElement"]>(undefined);
    const [openState, setOpenState] = useOptionallyControlledState<
      PopoverContextProps["openState"]
    >({
      controlledValue: open,
      defaultValue: defaultOpen,
    });
    const arrowRef = useRef<HTMLSpanElement>(null);
    const dialogContext = useContext(DialogContext);
    const [triggerHovered, setTriggerHovered] = useState(false);
    const [contentHovered, setContentHovered] = useState(false);
    // Track the actual computed placement after flip/shift middleware runs
    // (may differ from the placement prop if Floating UI adjusts it)
    const [actualPlacement, setActualPlacement] = useState<
      Placement | undefined
    >(placement);

    // TODO: Find a way to test these properly!
    // Stryker disable all : Currently relying on Chromatic test for all visuals
    const processMinHeight =
      typeof minHeight === "string" ? minHeight : `${minHeight}px`;

    usePopoverTransitionStates(popover, openState, {
      onOpenAnimationStart: useCallback(() => {
        onOpenAnimationStart?.();
      }, [onOpenAnimationStart]),
      onOpenAnimationComplete: useCallback(() => {
        onOpenAnimationComplete?.();
      }, [onOpenAnimationComplete]),
      onCloseAnimationStart: useCallback(() => {
        onCloseAnimationStart?.();
      }, [onCloseAnimationStart]),
      onCloseAnimationComplete: useCallback(() => {
        onCloseAnimationComplete?.();
      }, [onCloseAnimationComplete]),
    });

    /* istanbul ignore next */
    const updatePosition = useCallback(() => {
      if (!invoker || !popover) return;
      // disable position update when closing
      if (!openState) return;
      computePosition(invoker, popover, {
        placement: placement,
        middleware: [
          offset(8),
          size({
            padding: 5,
            apply({ rects, availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                ...(matchReferenceWidth
                  ? { width: `${rects.reference.width}px` }
                  : fixedWidth
                    ? { width: "40dvw" }
                    : {}),
                ...(fillAvailableHeight
                  ? {
                      height: `${availableHeight - 20}px`,
                      minHeight: processMinHeight,
                    }
                  : {}),
                ...(maxHeight
                  ? {
                      minHeight: processMinHeight,
                      height: fitScreen
                        ? typeof maxHeight === "string"
                          ? maxHeight
                          : `${maxHeight}px`
                        : undefined,
                      maxHeight: fitScreen
                        ? `${availableHeight - 20}px`
                        : typeof maxHeight === "string"
                          ? maxHeight
                          : `${maxHeight}px`,
                    }
                  : {}),
                ...(fitScreen && !maxHeight
                  ? {
                      minHeight: processMinHeight,
                      maxHeight: `${availableHeight - 20}px`,
                    }
                  : {}),
                zIndex: 1,
              });
            },
          }),
          flip({
            mainAxis: !disableFlip,
            crossAxis: false,
            fallbackAxisSideDirection: disableFlipFallback ? "none" : "end",
            padding: 5,
          }),
          shift({
            padding: 5,
            mainAxis: !disableShift,
            crossAxis: false,
            limiter: limitShift({
              mainAxis: !disableShift,
            }),
          }),
          arrow({
            element: !disableCaret && arrowElement ? arrowElement : null,
            padding: 5,
          }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        setActualPlacement(placement);
        const side = placement.split("-")[0];

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[side as "top" | "right" | "bottom" | "left"];

        const arrowDeg = () => {
          if (staticSide === "top") return 180;
          if (staticSide === "bottom") return 0;
          if (staticSide === "left") return 90;
          return 270;
        };

        Object.assign(popover.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        if (middlewareData.arrow && arrowElement) {
          const { x, y } = middlewareData.arrow;
          Object.assign(arrowElement.style, {
            left: x != null ? `${x}px` : "",
            top: y != null ? `${y}px` : "",
            right: "",
            bottom: "",
            [staticSide as string]: `${-arrowElement.offsetWidth + 2}px`,
            transform: `rotate(${arrowDeg()}deg)`,
          });
        }
      });
    }, [
      arrowElement,
      disableCaret,
      disableFlip,
      disableFlipFallback,
      disableShift,
      fillAvailableHeight,
      fitScreen,
      fixedWidth,
      invoker,
      matchReferenceWidth,
      maxHeight,
      openState,
      placement,
      popover,
      processMinHeight,
    ]);

    // Effect to add autoUpdate to eventListener
    // while the popover is opened
    useLayoutEffect(() => {
      if (!popover || !invoker) return;
      if (!openState) {
        return;
      }

      let requestID: number | null = null;
      const cleanup = autoUpdate(invoker, popover, () => {
        requestID = requestAnimationFrame(updatePosition);
      });

      return () => {
        if (requestID) cancelAnimationFrame(requestID);
        cleanup();
      };
    }, [invoker, popover, updatePosition, openState]);
    // Stryker restore all

    popover?.getRootNode();

    const popoverId = id ?? `popover-${uid.replace(/:/g, "")}`;
    const isControlled = typeof open !== "undefined";

    // Main function to open popover
    const openPopover = useCallback(() => {
      if (
        !popover ||
        !(rootNode as ShadowRoot | Document)?.querySelector(`#${popoverId}`)
      )
        return;
      setOpenState(true);
    }, [popover, rootNode, popoverId, setOpenState]);

    // Main function to close popover
    const closePopover = useCallback(
      (fn?: () => void) => {
        if (!popover || !openState) return;
        if (!openOnHover) onClose?.();
        setOpenState(false);

        // Reset hover states to prevent stuck state when closed programmatically
        // while mouse is still inside the popover content
        setTriggerHovered(false);
        setContentHovered(false);

        fn?.();
      },
      [
        popover,
        openState,
        openOnHover,
        onClose,
        setOpenState,
        setTriggerHovered,
        setContentHovered,
      ],
    );

    useImperativeHandle(
      ref,
      () => ({
        openPopover,
        closePopover,
      }),
      [openPopover, closePopover],
    );

    // Updating openness for controlled
    useEffect(() => {
      if (typeof openState === "undefined") return;
      if (openState) {
        if (dialogContext) {
          if (dialogContext?.ref?.current?.open) {
            safeShowPopover(popover);
            dialogContext?.setHasOpenPopover?.(true);
          }
          return;
        }
        safeShowPopover(popover);
      } else {
        safeHidePopover(popover);
        dialogContext?.setHasOpenPopover?.(false);
      }
    }, [openState, openPopover, closePopover, dialogContext, popover]);

    useOnClickOutside({
      disable: disableCloseOnClickOutside || !openState,
      onClickOutsideBegin: (e) => {
        if (!openState) return;
        onClickOutside?.(e);
      },
      targets: [invoker, popover],
      onClickOutside: () => {
        if (openOnHover || isControlled) return;
        closePopover();
      },
    });

    const value = useMemo(() => {
      return {
        popoverId,
        invoker,
        setInvoker,
        popover,
        setPopover,
        arrowElement,
        setArrowElement,
        openPopover,
        closePopover,
        placement,
        actualPlacement,
        onClose,
        openOnHover,
        openState,
        setOpenState,
        disableCloseOnClickOutside,
        fillAvailableHeight,
        maxHeight,
        matchReferenceWidth,
        fixedWidth,
        disableCaret,
        disableFlipFallback,
        disableTriggerFocus,
        disableCloseOnEscape,
        noPadding,
        controlled: isControlled,
        modal: modal,
        arrowRef,
        rootNode,
        setRootNode,
        delay,
        triggerHovered,
        setTriggerHovered,
        contentHovered,
        setContentHovered,
      };
    }, [
      popoverId,
      invoker,
      popover,
      arrowElement,
      openPopover,
      closePopover,
      placement,
      actualPlacement,
      onClose,
      openOnHover,
      openState,
      setOpenState,
      disableCloseOnClickOutside,
      fillAvailableHeight,
      maxHeight,
      matchReferenceWidth,
      fixedWidth,
      disableCaret,
      disableFlipFallback,
      disableTriggerFocus,
      disableCloseOnEscape,
      noPadding,
      isControlled,
      modal,
      rootNode,
      delay,
      triggerHovered,
      setTriggerHovered,
      contentHovered,
      setContentHovered,
    ]);

    return (
      <PopoverContext.Provider value={value}>
        {children}
      </PopoverContext.Provider>
    );
  }),
  {
    /**
     * PopoverTrigger component for creating custom trigger elements.
     *
     * Features:
     * - Supports custom trigger elements via render prop pattern
     * - Automatic focus management and keyboard interaction
     * - Hover and click event handling
     * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
     * - Context integration with parent popover
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Popover.Trigger>
     *   {(props) => (
     *     <button {...props}>
     *       Click to open popover
     *     </button>
     *   )}
     * </Popover.Trigger>
     */
    Trigger: PopoverTrigger,
    /**
     * PopoverContent component for the main popover content area.
     *
     * Features:
     * - Automatic positioning and sizing based on trigger
     * - Screen boundary detection and fitting
     * - Scroll handling with custom scroller reference
     * - Focus management and keyboard navigation
     * - Hover interaction support for tooltip mode
     * - Escape key handling for closing
     * - Tab key trapping for modal popovers
     * - Arrow/caret indicator with automatic positioning
     * - Context integration with parent popover
     * - Accessibility support with proper ARIA roles
     *
     * @example
     * <Popover.Content>
     *   <p>This is the popover content</p>
     *   <Popover.Close>Close</Popover.Close>
     * </Popover.Content>
     */
    Content: PopoverContent,
    /**
     * PopoverClose component for closing the popover.
     *
     * Features:
     * - Automatic popover closing on click
     * - Supports all Button component props and styling
     * - Controlled and uncontrolled mode support
     * - Context integration with parent popover
     * - Accessibility support with proper button semantics
     * - Delayed closing for controlled mode
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Popover.Close>Close</Popover.Close>
     */
    Close: PopoverClose,
    /**
     * PopoverButton component for creating button-style triggers.
     *
     * Features:
     * - Button styling and behavior with all Button props
     * - Automatic popover opening on click
     * - Hover interaction support for tooltip mode
     * - Focus management and keyboard navigation
     * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
     * - Context integration with parent popover
     * - Event handling for mouse, keyboard, and focus events
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Popover.Button>Click to open</Popover.Button>
     */
    Button: PopoverButton,
  },
);
Popover.displayName = "Popover";
